export interface DeviceFingerprint {
  level: number;
  // Nivel 1 (Standard)
  userAgent?: string;
  language?: string;
  timezoneOffset?: number;
  screenResolution?: string;
  colorDepth?: number;
  cookiesEnabled?: boolean;
  // Nivel 2 (Hardware)
  hardwareConcurrency?: number;
  deviceMemory?: number;
  // Nivel 3 (Deep Fingerprint)
  canvasFingerprint?: string;
  webGLFingerprint?: string;
  audioContextFingerprint?: string;
}

/**
 * Captures device fingerprint based on the requested level as defined in the technical contract.
 * @param level 1 (Standard), 2 (Hardware), 3 (Deep WebGL/Audio)
 */
export async function captureDeviceFingerprint(level: number): Promise<DeviceFingerprint> {
  const fp: DeviceFingerprint = { level };

  if (typeof window === 'undefined') return fp;

  // Level 1: Standard Regional & Display tracking
  if (level >= 1) {
    fp.userAgent = navigator.userAgent;
    fp.language = navigator.language;
    fp.timezoneOffset = new Date().getTimezoneOffset();
    fp.screenResolution = `${window.screen.width}x${window.screen.height}`;
    fp.colorDepth = window.screen.colorDepth;
    fp.cookiesEnabled = navigator.cookieEnabled;
  }

  // Level 2: Hardware specific metrics
  if (level >= 2) {
    fp.hardwareConcurrency = navigator.hardwareConcurrency;
    fp.deviceMemory = (navigator as any).deviceMemory; // Not supported on all browsers
  }

  // Level 3: Deep mathematical & rendering signatures
  if (level >= 3) {
    fp.canvasFingerprint = generateCanvasFingerprint();
    fp.webGLFingerprint = generateWebGLFingerprint();
    fp.audioContextFingerprint = await generateAudioFingerprint();
  }

  return fp;
}

function generateCanvasFingerprint(): string {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return 'unsupported';

    canvas.width = 200;
    canvas.height = 50;

    // Text with different fonts and baseline to capture rendering differences
    ctx.textBaseline = 'top';
    ctx.font = '14px "Arial"';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);

    ctx.fillStyle = '#069';
    ctx.fillText('Hello, world!', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('Hello, world!', 4, 17);

    const dataURL = canvas.toDataURL();
    return simpleHash(dataURL);
  } catch (e) {
    return 'error';
  }
}

function generateWebGLFingerprint(): string {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return 'unsupported';
    const debugInfo = (gl as WebGLRenderingContext).getExtension('WEBGL_debug_renderer_info');
    if (!debugInfo) return 'unsupported';
    const vendor = (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
    const renderer = (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
    return simpleHash(`${vendor}~${renderer}`);
  } catch (e) {
    return 'error';
  }
}

async function generateAudioFingerprint(): Promise<string> {
  try {
    const AudioContext = window.OfflineAudioContext || (window as any).webkitOfflineAudioContext;
    if (!AudioContext) return 'unsupported';
    const context = new AudioContext(1, 44100, 44100);
    const oscillator = context.createOscillator();
    oscillator.type = 'triangle';
    oscillator.frequency.setValueAtTime(10000, context.currentTime);
    const compressor = context.createDynamicsCompressor();
    compressor.threshold.setValueAtTime(-50, context.currentTime);
    compressor.knee.setValueAtTime(40, context.currentTime);
    compressor.ratio.setValueAtTime(12, context.currentTime);
    compressor.attack.setValueAtTime(0, context.currentTime);
    compressor.release.setValueAtTime(0.25, context.currentTime);
    oscillator.connect(compressor);
    compressor.connect(context.destination);
    oscillator.start(0);
    
    return new Promise((resolve) => {
      context.oncomplete = (event) => {
        let hash = 0;
        const channelData = event.renderedBuffer.getChannelData(0);
        // sample a small window to avoid huge loops
        for (let i = 4500; i < 5000; i += 10) {
          hash += Math.abs(channelData[i]);
        }
        resolve(simpleHash(hash.toString()));
      };
      context.startRendering().catch(() => resolve('error'));
    });
  } catch (e) {
    return 'error';
  }
}

/**
 * Simple hash function to compress the signatures
 */
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash.toString(16);
}
